import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar, Button, Card, ErrorState, Icon, IconButton, MenuDropdown, PageHeader, Skeleton, useToast } from '../../../components/ui';
import { FilterMenu } from '../../../components/common/FilterMenu/FilterMenu';
import { StatusBadge } from '../../../components/common/Parts/Parts';
import { useAuth } from '../../../app/providers/AuthProvider';
import { cn } from '../../../utils/cn';
import { JOB_STATUS, PRIORITY } from '../../../constants/status';
import { labelOf } from '../../../utils/labels';
import { formatDate } from '../../../utils/date';
import { ACTION_LABEL, allowedMoves, OPEN_STATUSES } from '../constants/jobFlow';
import { masterLabel } from '../../masters/services/masterService';
import { useMasters } from '../../masters/hooks/useMasters';
import { loadSettings } from '../../settings/services/settingsService';
import { jobService } from '../services/jobService';
import { teamService } from '../../team/services/teamService';
import type { DragEvent } from 'react';
import type { Job, JobStatus } from '../types/jobTypes';

interface BoardColumn {
  key: JobStatus;
  title: string;
  dot: string;
  /** Recent history only: nothing can be dropped here */
  readOnly?: boolean;
}

const COLUMNS: BoardColumn[] = [
  { key: 'pending', title: labelOf(JOB_STATUS, 'pending'), dot: 'bg-navy-300' },
  { key: 'assigned', title: labelOf(JOB_STATUS, 'assigned'), dot: 'bg-brand-600' },
  { key: 'in_progress', title: labelOf(JOB_STATUS, 'in_progress'), dot: 'bg-sky-500' },
  { key: 'on_hold', title: labelOf(JOB_STATUS, 'on_hold'), dot: 'bg-sun-500' },
  { key: 'completed', title: labelOf(JOB_STATUS, 'completed'), dot: 'bg-emerald-500' },
  { key: 'invoiced', title: `${labelOf(JOB_STATUS, 'invoiced')} (recent)`, dot: 'bg-navy-800', readOnly: true },
];
const PRIORITY_RANK: Record<string, number> = { urgent: 0, high: 1, normal: 2, low: 3 };
const DAY = 86400000;
const startOfToday = () => new Date(new Date().setHours(0, 0, 0, 0));

/** Days from today to the visit date: negative = overdue, 0 = today. null when not scheduled. */
const visitIn = (job: Job): number | null => (job.scheduledDate ? Math.round((new Date(job.scheduledDate).setHours(0, 0, 0, 0) - startOfToday().getTime()) / DAY) : null);
const isOverdue = (job: Job) => {
  const days = visitIn(job);
  return OPEN_STATUSES.includes(job.status) && days != null && days < 0;
};

const byPriorityThenDate = (a: Job, b: Job) =>
  (PRIORITY_RANK[a.priority] ?? 2) - (PRIORITY_RANK[b.priority] ?? 2) ||
  (a.scheduledDate ? new Date(a.scheduledDate).getTime() : Infinity) - (b.scheduledDate ? new Date(b.scheduledDate).getTime() : Infinity);

function VisitDate({ job }: { job: Job }) {
  const d = visitIn(job);
  if (d == null) return <span className="text-ink-400">No visit date</span>;
  const late = isOverdue(job);
  const text = d === 0 ? 'Today' : d === 1 ? 'Tomorrow' : d === -1 ? 'Yesterday' : formatDate(job.scheduledDate);
  return (
    <span className={cn(late ? 'font-medium text-red-600' : d === 0 ? 'font-medium text-brand-700' : 'text-ink-500')}>{late && d < -1 ? `${text} · ${-d} days late` : text}</span>
  );
}

function JobCard({ job, isStaff, canDrag, onDragStart, onDragEnd, onMove, onInvoice, dragging }: {
  job: Job;
  isStaff: boolean;
  canDrag: boolean;
  onDragStart: (e: DragEvent, job: Job) => void;
  onDragEnd: () => void;
  onMove: (job: Job, to: JobStatus) => void;
  onInvoice: (job: Job) => void;
  dragging: boolean;
}) {
  const navigate = useNavigate();
  const moves = allowedMoves(job.status, isStaff);
  const ours = (job.materials ?? []).filter((m) => m.supplyBy === 'us');
  const issued = ours.filter((m) => (m.issuedQty ?? 0) > 0).length;
  const menu = [
    { label: 'Open job', icon: 'eye', onClick: () => navigate(`/jobs/${job._id}`) },
    isStaff && !['invoiced', 'cancelled'].includes(job.status) && { label: 'Edit', icon: 'edit', onClick: () => navigate(`/jobs/${job._id}/edit`) },
    isStaff && job.status === 'completed' && !job.invoiceId && { label: 'Create invoice', icon: 'receipt', onClick: () => onInvoice(job) },
    isStaff && job.invoiceId && { label: 'Open invoice', icon: 'receipt', onClick: () => navigate(`/invoices/${job.invoiceId}`) },
    moves.length > 0 && { divider: true },
    moves.length > 0 && { heading: 'Move to' },
    ...moves.map((s) => ({
      label: s === 'cancelled' ? ACTION_LABEL.cancelled?.[0] : labelOf(JOB_STATUS, s),
      icon: ACTION_LABEL[s]?.[1] ?? 'arrowRight',
      danger: s === 'cancelled',
      onClick: () => onMove(job, s),
    })),
  ].filter(Boolean);

  return (
    <article
      draggable={canDrag}
      onDragStart={(e) => onDragStart(e, job)}
      onDragEnd={onDragEnd}
      className={cn(
        'group rounded-lg border border-line bg-white p-3 shadow-card transition-[border-color,opacity,box-shadow]',
        canDrag && 'cursor-grab active:cursor-grabbing',
        dragging ? 'opacity-40' : 'hover:border-navy-300 hover:shadow-soft',
        isOverdue(job) && 'border-l-[3px] border-l-red-500',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <Link to={`/jobs/${job._id}`} className="font-mono text-[11px] text-ink-400 hover:text-brand-600" draggable={false}>
          {job.serviceNo}
        </Link>
        <div className="-mr-1 -mt-1 flex items-center gap-1">
          {job.priority && job.priority !== 'normal' && <StatusBadge map={PRIORITY} value={job.priority} className="text-[11px]" />}
          <MenuDropdown width={210} trigger={<IconButton icon="moreH" size="sm" label={`Actions for ${job.serviceNo}`} className="h-7 w-7" />} items={menu} />
        </div>
      </div>
      <Link to={`/jobs/${job._id}`} draggable={false} className="mt-1 block outline-none focus-visible:ring-2 focus-visible:ring-steel">
        <p className="text-sm font-semibold leading-snug text-navy-900">{job.customer?.name}</p>
        {job.title && <p className="mt-0.5 line-clamp-2 text-[13px] leading-snug text-ink-600">{job.title}</p>}
      </Link>
      <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="inline-flex items-center gap-1">
          <Icon name="calendar" size={13} className="text-ink-400" />
          <VisitDate job={job} />
        </span>
        {ours.length > 0 && (
          <span className="inline-flex items-center gap-1 text-ink-500" title="Material lines issued from stock">
            <Icon name="box" size={13} className="text-ink-400" />
            {issued}/{ours.length}
          </span>
        )}
      </div>
      <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-line pt-2.5">
        {job.assignedTo?.length ? (
          <span className="flex items-center" title={job.assignedTo.map((u) => u.name).join(', ')}>
            {job.assignedTo.slice(0, 3).map((u, i) => (
              <Avatar key={u._id} name={u.name} size={24} className={cn('ring-2 ring-white', i > 0 && '-ml-1.5')} />
            ))}
            <span className="ml-1.5 max-w-[110px] truncate text-xs text-ink-500">
              {job.assignedTo.length === 1 ? job.assignedTo[0].name.split(' ')[0] : `${job.assignedTo.length} workers`}
            </span>
          </span>
        ) : (
          <span className="text-xs text-ink-400">Unassigned</span>
        )}
        <span className="truncate text-[11px] text-ink-400">{masterLabel('serviceType', job.serviceType)}</span>
      </div>
      {isStaff && job.status === 'completed' && !job.invoiceId && (
        <Button size="sm" variant="outline" leftIcon="receipt" fullWidth className="mt-2.5" onClick={() => onInvoice(job)}>
          Create invoice
        </Button>
      )}
    </article>
  );
}

/** Work board: service jobs as cards in status columns. Drag a card, or use its menu, to move it. */
export default function JobBoard() {
  const navigate = useNavigate();
  const toast = useToast();
  const { isStaff } = useAuth();
  useMasters();
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [search, setSearch] = useState('');
  const [worker, setWorker] = useState({ id: '', label: '' });
  const [priority, setPriority] = useState('');
  const [due, setDue] = useState('');
  const [drag, setDrag] = useState<Job | null>(null); // job being dragged
  const [over, setOver] = useState<string | null>(null); // column under the pointer
  const [gstin, setGstin] = useState('');

  const load = useCallback(async () => {
    try {
      const [open, invoiced] = await Promise.all([
        jobService.list({ status: [...OPEN_STATUSES, 'completed'].join(','), limit: 200 }),
        jobService.list({ status: 'invoiced', limit: 15, sort: '-createdAt' }),
      ]);
      setJobs([...open.data, ...invoiced.data]);
      setError(null);
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    load();
    if (isStaff)
      loadSettings()
        .then((s) => setGstin(s.gstin || ''))
        .catch(() => {});
    // Keep the board fresh while it is open (workers update jobs from site).
    const t = setInterval(() => document.visibilityState === 'visible' && load(), 60000);
    return () => clearInterval(t);
  }, [load, isStaff]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (jobs ?? []).filter((j) => {
      if (q && !`${j.serviceNo} ${j.customer?.name ?? ''} ${j.title ?? ''} ${j.customer?.phone ?? ''}`.toLowerCase().includes(q)) return false;
      if (worker.id && !(j.assignedTo ?? []).some((u) => u._id === worker.id)) return false;
      if (priority && (j.priority ?? 'normal') !== priority) return false;
      const d = visitIn(j);
      if (due === 'overdue' && !isOverdue(j)) return false;
      if (due === 'today' && d !== 0) return false;
      if (due === 'week' && !(d != null && d >= 0 && d <= 7)) return false;
      if (due === 'none' && d != null) return false;
      return true;
    });
  }, [jobs, search, worker, priority, due]);

  const columns = useMemo(
    () => COLUMNS.map((c) => ({ ...c, jobs: filtered.filter((j) => j.status === c.key).sort(c.key === 'invoiced' ? () => 0 : byPriorityThenDate) })),
    [filtered],
  );

  const move = async (job, to) => {
    if (job.status === to) return;
    if (!allowedMoves(job.status, isStaff).includes(to)) {
      toast.error(`A job can't go from ${labelOf(JOB_STATUS, job.status)} to ${labelOf(JOB_STATUS, to)}`);
      return;
    }
    if (to === 'assigned' && !job.assignedTo?.length) {
      toast.info('Pick the workers for this job first');
      navigate(`/jobs/${job._id}`);
      return;
    }
    const before = job.status;
    setJobs((list) => list && list.map((j) => (j._id === job._id ? { ...j, status: to } : j))); // show it moved straight away
    try {
      await jobService.setStatus(job._id, to);
      toast.success(`${job.serviceNo}: ${labelOf(JOB_STATUS, to)}`);
      load();
    } catch (err) {
      setJobs((list) => list && list.map((j) => (j._id === job._id ? { ...j, status: before } : j)));
      toast.error(err.message);
    }
  };

  const createInvoice = async (job) => {
    try {
      const data = await jobService.createInvoice(job._id, gstin ? 'gst' : 'non_gst');
      toast.success('Draft invoice created');
      navigate(`/invoices/${data._id}`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const canDropOn = (key: JobStatus) => drag && drag.status !== key && !COLUMNS.find((c) => c.key === key)?.readOnly && allowedMoves(drag.status, isStaff).includes(key);
  const open = (jobs ?? []).filter((j) => OPEN_STATUSES.includes(j.status));
  const stats: [label: string, value: number, filter: string | null][] = [
    ['Open jobs', open.length, ''],
    ['Visits today', open.filter((j) => visitIn(j) === 0).length, 'today'],
    ['Overdue visits', open.filter(isOverdue).length, 'overdue'],
    ['To invoice', (jobs ?? []).filter((j) => j.status === 'completed' && !j.invoiceId).length, null],
  ];
  const anyFilter = search || worker.id || priority || due;

  return (
    <>
      <PageHeader
        title={isStaff ? 'Work board' : 'My work board'}
        subtitle="Drag a job to move it along, or use the ••• menu on the card."
        actions={
          <>
            <Button variant="outline" leftIcon="menu" onClick={() => navigate('/jobs')}>
              List view
            </Button>
            <IconButton icon="refresh" label="Refresh" variant="outline" onClick={load} />
            {isStaff && (
              <Button leftIcon="plus" onClick={() => navigate('/jobs/new')}>
                New service job
              </Button>
            )}
          </>
        }
      />

      <div className="mb-4 grid grid-cols-2 gap-3 xl:grid-cols-4">
        {stats.map(([label, value, filter]) => (
          <button
            key={label}
            type="button"
            disabled={filter === null}
            onClick={() => setDue(filter === due ? '' : filter ?? '')}
            className={cn(
              'rounded-xl border bg-white px-4 py-3 text-left shadow-card outline-none transition-colors focus-visible:ring-2 focus-visible:ring-steel disabled:cursor-default',
              filter && filter === due ? 'border-brand-400 bg-brand-50/60' : 'border-line hover:border-navy-300',
            )}
          >
            <p className="text-[13px] text-ink-500">{label}</p>
            <p className={cn('tabular text-xl font-semibold text-navy-900', label === 'Overdue visits' && value > 0 && 'text-red-600')}>{jobs ? value : '…'}</p>
          </button>
        ))}
      </div>

      <Card className="mb-4 flex flex-wrap items-center gap-2 p-3">
        <div className="relative w-full sm:w-72">
          <Icon name="search" size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Job no, customer, title"
            aria-label="Search jobs"
            className="h-9 w-full rounded-lg border border-line bg-white pl-9 pr-3 text-sm outline-none placeholder:text-ink-300 hover:border-navy-300 focus:border-brand-500 focus:ring-4 focus:ring-brand-500/15"
          />
        </div>
        {isStaff && (
          <FilterMenu
            label="Worker"
            icon="users"
            value={worker.id}
            valueLabel={worker.label}
            onChange={(id, label) => setWorker({ id, label: label ?? '' })}
            fetcher={(q) => teamService.search(q, { role: 'worker' })}
          />
        )}
        <FilterMenu
          label="Priority"
          icon="alert"
          value={priority}
          onChange={(v) => setPriority(v)}
          options={Object.entries(PRIORITY).map(([value, [label]]) => ({ value, label }))}
        />
        <FilterMenu
          label="Visit"
          icon="calendar"
          value={due}
          onChange={(v) => setDue(v)}
          options={[
            { value: 'overdue', label: 'Overdue' },
            { value: 'today', label: 'Today' },
            { value: 'week', label: 'Next 7 days' },
            { value: 'none', label: 'No visit date' },
          ]}
        />
        {anyFilter && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setSearch('');
              setWorker({ id: '', label: '' });
              setPriority('');
              setDue('');
            }}
          >
            Clear all
          </Button>
        )}
      </Card>

      {error ? (
        <Card>
          <ErrorState error={error} onRetry={load} />
        </Card>
      ) : !jobs ? (
        <div className="flex gap-4 overflow-hidden">
          {COLUMNS.map((c) => (
            <Skeleton key={c.key} className="h-96 w-72 shrink-0 rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8" aria-label="Job board">
          {columns.map((col, i) => {
            const droppable = canDropOn(col.key);
            return (
              <Fragment key={col.key}>
                {/* Divider line between columns */}
                {i > 0 && <div aria-hidden="true" className="w-px shrink-0 self-stretch bg-navy-200" />}
                <section
                  aria-label={col.title}
                  onDragOver={(e) => {
                    if (!droppable) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (over !== col.key) setOver(col.key);
                  }}
                  onDragLeave={(e) => !e.currentTarget.contains(e.relatedTarget as Node | null) && setOver(null)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setOver(null);
                    if (drag && droppable) move(drag, col.key);
                    setDrag(null);
                  }}
                  className={cn(
                    'flex w-[290px] shrink-0 flex-col rounded-xl border bg-navy-50/70 transition-colors',
                    drag && droppable ? 'border-dashed border-brand-400 bg-brand-50/50' : 'border-transparent',
                    drag && !droppable && drag.status !== col.key && 'opacity-50',
                    over === col.key && 'border-brand-600 bg-brand-50',
                  )}
                >
                  <header className="flex items-center gap-2 px-3 pb-2 pt-3">
                    <span className={cn('h-2 w-2 rounded-full', col.dot)} />
                    <h2 className="flex-1 text-[13px] font-semibold uppercase tracking-wide text-ink-600">{col.title}</h2>
                    <span className="tabular rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-ink-500 shadow-soft">{col.jobs.length}</span>
                  </header>
                  <div className="flex max-h-[calc(100vh-330px)] min-h-[140px] flex-col gap-2.5 overflow-y-auto px-2.5 pb-3">
                    {col.jobs.map((job) => (
                      <JobCard
                        key={job._id}
                        job={job}
                        isStaff={isStaff}
                        canDrag={!col.readOnly && allowedMoves(job.status, isStaff).length > 0}
                        dragging={drag?._id === job._id}
                        onDragStart={(e, j) => {
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData('text/plain', j._id);
                          setDrag(j);
                        }}
                        onDragEnd={() => {
                          setDrag(null);
                          setOver(null);
                        }}
                        onMove={move}
                        onInvoice={createInvoice}
                      />
                    ))}
                    {col.jobs.length === 0 && (
                      <p
                        className={cn('rounded-lg border border-dashed px-3 py-6 text-center text-xs', droppable ? 'border-brand-400 text-brand-600' : 'border-line text-ink-400')}
                      >
                        {droppable ? 'Drop here' : anyFilter ? 'No matching jobs' : 'No jobs'}
                      </p>
                    )}
                  </div>
                </section>
              </Fragment>
            );
          })}
        </div>
      )}
      {jobs && <p className="mt-1 text-xs text-ink-400">Cancelled jobs are not shown. Invoiced shows the latest 15.</p>}
    </>
  );
}
